import { useCallback } from 'react'

import {
  COMMON_MIXPANEL_PROPERTIES,
  CUSTOM_PREFERENCES_EVENTS,
  S4C_PROPERTIES,
} from '../../common/constants/mixpanelConstants'
import { MEMBER_PREFERENCE_NAMES, ProviderPreferenceValues } from '../../memberPreferences/constants'
import { MemberPreferencesForUserV2, ParsedPreferenceKey } from '../../memberPreferences/types'
import {
  getBipocUserPreferenceSelection,
  getPreferenceCategoryForMixpanel,
  getProviderPreferenceKeyFromParsedKey,
  removePreferenceValueFromParsedPreferences,
} from '../../memberPreferences/utils'

export interface UseRemoveMatchedPreferenceArgs {
  memberPreferencesV2: MemberPreferencesForUserV2 | null
  /** Callback to save updated preferences. Callsite handles userId and Redux state updates. */
  onSavePreferences: (updatedPreferences: MemberPreferencesForUserV2) => void
  setCurrentPreferences?: React.Dispatch<React.SetStateAction<ProviderPreferenceValues>>
  trackEvent?: (eventName: string, properties?: Record<string, any>) => void
  /** Optional page context for tracking (e.g., 'preferences category page' or 'recommendations explained page') */
  page?: string
  userCountryIsoCode?: string
}

export const useRemoveMatchedPreference = ({
  memberPreferencesV2,
  onSavePreferences,
  setCurrentPreferences,
  trackEvent,
  page,
  userCountryIsoCode,
}: UseRemoveMatchedPreferenceArgs) => {
  return useCallback(
    ({ preferenceKey, valueId }: { preferenceKey: ParsedPreferenceKey; valueId: string | undefined }) => {
      if (!memberPreferencesV2) return

      // 1. Calculate updated preferences
      const basePreferences = removePreferenceValueFromParsedPreferences(
        memberPreferencesV2,
        preferenceKey,
        valueId,
      ) as MemberPreferencesForUserV2

      // Recalculate isBipoc when ethnicities are modified to maintain data consistency
      const updatedPreferences = {
        ...basePreferences,
        ...(preferenceKey === MEMBER_PREFERENCE_NAMES.ETHNICITIES && {
          isBipoc: getBipocUserPreferenceSelection({ userPreferenceSelections: basePreferences.ethnicities }),
        }),
      }

      // 2. Update local accordion state (if provided - used in custom preferences modal)
      if (setCurrentPreferences) {
        const targetKey = getProviderPreferenceKeyFromParsedKey(preferenceKey)

        setCurrentPreferences((prev) => {
          // Handle availabilities specially - empty state is null, not []
          if (preferenceKey === MEMBER_PREFERENCE_NAMES.AVAILABILITIES) {
            return { ...prev, [targetKey]: null }
          }
          const currentValue = prev[targetKey]
          if (!Array.isArray(currentValue)) return prev
          // When valueId is undefined, clear the entire preference
          if (!valueId) {
            return { ...prev, [targetKey]: [] }
          }
          return { ...prev, [targetKey]: currentValue.filter((v) => v !== valueId) }
        })
      }

      // 3. Save to backend and update Redux state (handled by callsite)
      onSavePreferences(updatedPreferences)

      // 4. Track removal event (tracks user action, not persistence result)
      if (trackEvent && page && userCountryIsoCode) {
        const preferenceCategory = getPreferenceCategoryForMixpanel({
          userCountryIsoCode,
          preferenceName: preferenceKey,
        })
        trackEvent(CUSTOM_PREFERENCES_EVENTS.CLICK_REMOVE_PROVIDER_PREFERENCE, {
          [S4C_PROPERTIES.PREFERENCE_CATEGORY]: preferenceCategory,
          [COMMON_MIXPANEL_PROPERTIES.PAGE]: page,
        })
      }
    },
    [memberPreferencesV2, onSavePreferences, setCurrentPreferences, trackEvent, page, userCountryIsoCode],
  )
}
